import React, { useRef } from "react";
import { Modal } from "antd";

import Iconfont from "@components/iconfont/iconfont";
import OverlayScrollbar from "@components/overlay_scrollbar";
import { def_team_logo } from "@util/enum";

/**
 * 官网价格页入口没有引入 antd 基础样式（antd/dist/antd.less），
 * `.ant-modal-mask` 拿不到 position/inset，只设 backgroundColor 会是 0 尺寸的空 div。
 * 这里用 maskStyle 在行内把遮罩补齐，保证弹窗出现时整屏铺 #000 50% 遮罩。
 */
const MASK_STYLE = {
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  /* 与 .official-price-subscribe-modal.ant-modal-wrap 的 z-index 一致；
   * 遮罩在 DOM 中位于 wrap 之前，同层级下弹窗仍在其上方。
   * 需要盖住官网固定导航（.nav z-index: 100）与对比表吸顶头（z-index: 99）。 */
  zIndex: 1000,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
};

function getCurrentLanguage() {
  const rawLanguage = sessionStorage.getItem("lang") || window.lang || "zh";
  const primaryLanguage = rawLanguage.split(",")[0];
  if (primaryLanguage.indexOf("zh") === 0) return "zh";
  return primaryLanguage.split("-")[0] || "zh";
}

function getTeamName(team) {
  const language = getCurrentLanguage();
  return team?.mlName?.[language] || team?.mlName?.zh || team?.name || "--";
}

function getTeamDescription(team) {
  return team?.note || team?.signature || "";
}

export default function ManageTeamSelectModal({
  teamVisible,
  teamList,
  onCancel,
  onSelectTeam,
}) {
  const modalBodyRef = useRef(null);
  const teamGridRef = useRef(null);

  return (
    <Modal
      visible={teamVisible}
      width={620}
      centered
      footer={null}
      closable={false}
      destroyOnClose
      wrapClassName="official-price-subscribe-modal official-price-subscribe-modal--team"
      maskStyle={MASK_STYLE}
      onCancel={onCancel}
    >
      <div className="official-price-subscribe-modal__header">
        <div className="official-price-subscribe-modal__title">
          选择私域
        </div>
        <button
          type="button"
          className="official-price-subscribe-modal__close"
          aria-label="关闭"
          onClick={onCancel}
        >
          <Iconfont type="icon-a-guanbixi" />
        </button>
      </div>
      <div className="official-price-team-modal__body" ref={modalBodyRef}>
        <div
          className="official-price-team-modal__grid overlay-scrollbar-hidden"
          ref={teamGridRef}
        >
          {(teamList || []).map((team) => {
            const name = getTeamName(team);
            const description = getTeamDescription(team);
            const showInternational = team?.model === 2 || team?.bindingFlag;

            return (
              <button
                type="button"
                key={team.id}
                className="official-price-team-modal__item"
                onClick={() => onSelectTeam(team)}
              >
                {showInternational && (
                  <div className="official-price-team-modal__badge">
                    <Iconfont type="icon-haiwaiban-baise" />
                    <span>Int&apos;l</span>
                  </div>
                )}
                <div className="official-price-team-modal__logo-wrap">
                  <img
                    className="official-price-team-modal__logo"
                    src={team?.logo || def_team_logo}
                    alt=""
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = def_team_logo;
                    }}
                  />
                </div>
                <div
                  className="official-price-team-modal__name"
                  title={name}
                >
                  {name}
                </div>
                <div
                  className="official-price-team-modal__desc"
                  title={description}
                >
                  {description}
                </div>
              </button>
            );
          })}
        </div>
        {/* 原生滚动条隐藏，改用悬浮滚动条：贴在距弹窗右侧 6px 的位置 */}
        <OverlayScrollbar
          containerRef={modalBodyRef}
          targetRef={teamGridRef}
          size={4}
          gap={6}
        />
      </div>
    </Modal>
  );
}
